import React, { Suspense, useRef, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { ScrollControls, useScroll, Environment, Lightformer, Html, Text } from '@react-three/drei';
import * as THREE from 'three';
import { easing } from 'maath';

import Wheel from './Wheel';

import HeroLogo from '../HeroLogo';
import OverlayUI from '../OverlayUI';
import DetailView from '../DetailView';
import RollerCoaster from '../RollerCoaster';
import ParallaxBackground from '../ParallaxBackground';
import SponsorCarousel from '../SponsorCarousel';

import useStore from '../../store';

const ResponsiveCamera = () => {
    const { size, camera } = useThree();

    useEffect(() => {
        const aspect = size.width / size.height;
        if (aspect < 1) {
            camera.fov = 80;
            camera.position.z = 35;
        } else {
            // Desktop / Landscape
            camera.fov = 45;
            camera.position.z = 18;
        }
        camera.updateProjectionMatrix();
    }, [size, camera]);

    return null;
};

const ScrollAnimator = ({ wheelRef, carouselRef }) => {
    const scroll = useScroll();

    // Store State
    const stage = useStore((state) => state.stage);
    const setStage = useStore((state) => state.setStage);
    const targetSection = useStore((state) => state.targetSection);
    const setTargetSection = useStore((state) => state.setTargetSection);

    // Handle Programmatic Scroll
    useEffect(() => {
        if (!targetSection || !scroll.el) return;

        // If it's a "Mode" switch like tickets, we handle it via router now in Navbar
        if (targetSection === 'tickets') return;

        let targetOffset = 0;
        if (targetSection === 'events') targetOffset = 0.35;
        else if (targetSection === 'sponsors') targetOffset = 0.8;
        else if (targetSection === 'hero') {
            targetOffset = 0;
            setStage('intro');
        }

        const targetTop = targetOffset * (scroll.el.scrollHeight - scroll.el.clientHeight);

        scroll.el.scrollTo({
            top: targetTop,
            behavior: targetSection === 'hero' ? 'auto' : 'smooth'
        });

        setTargetSection(null);

    }, [targetSection, scroll.el, setTargetSection, setStage]);

    useFrame((state, delta) => {
        // If Zoomed, override scroll control
        if (stage === 'zoomed') {
            easing.damp3(state.camera.position, [-1, 0, 4], 0.5, delta);
            easing.damp3(state.camera.rotation, [0, 0, 0], 0.5, delta);
            return;
        }

        const offset = scroll.offset;

        // --- 1. Animation & Scaling ---
        const isMobile = window.innerWidth < 768;

        if (wheelRef.current) {
            const t = THREE.MathUtils.smoothstep(offset, 0.05, 0.25);
            const currentY = THREE.MathUtils.lerp(-25, 0, t);

            const targetX = isMobile ? 6.5 : 5;
            wheelRef.current.position.x = THREE.MathUtils.lerp(targetX, targetX, t);

            wheelRef.current.position.y = currentY;

            const maxScale = isMobile ? 1.2 : 1;
            const currentScale = THREE.MathUtils.lerp(0.2, maxScale, t);
            wheelRef.current.scale.setScalar(currentScale);
        }

        if (carouselRef.current) {
            const carouselProgress = THREE.MathUtils.smoothstep(offset, 0.4, 0.6);
            carouselRef.current.scale.setScalar(carouselProgress * 1.5);
        }

        // --- 2. Camera Spline / Keyframes ---
        const pos1 = new THREE.Vector3(0, 0, 20);
        const look1 = new THREE.Vector3(0, 0, 0);

        const pos2 = new THREE.Vector3(3, -2, 16);
        const look2 = new THREE.Vector3(9, 0, -2);

        const pos3 = new THREE.Vector3(0, -40, 40);
        const look3 = new THREE.Vector3(0, -40, 0);

        let targetPos = new THREE.Vector3();
        let targetLook = new THREE.Vector3();

        if (offset < 0.4) {
            const t = THREE.MathUtils.smoothstep(offset, 0, 0.25);
            targetPos.lerpVectors(pos1, pos2, t);
            targetLook.lerpVectors(look1, look2, t);

            if (offset < 0.1 && stage !== 'intro') setStage('intro');
            if (offset >= 0.1 && stage !== 'wheel') setStage('wheel');
        } else {
            // Updated range for new position
            const t = THREE.MathUtils.smoothstep(offset, 0.65, 0.75);


            targetPos.lerpVectors(pos2, pos3, t);
            targetLook.lerpVectors(look2, look3, t);

            if (offset > 0.7 && stage !== 'sponsors') setStage('sponsors');
            if (offset <= 0.7 && stage !== 'wheel') setStage('wheel');
        }

        easing.damp3(state.camera.position, targetPos, 0.5, delta);
        const currentLook = new THREE.Vector3(0, 0, -1).applyQuaternion(state.camera.quaternion).add(state.camera.position);
        easing.damp3(currentLook, targetLook, 0.5, delta);
        state.camera.lookAt(currentLook);
    });

    return null;
}

const FooterAnimator = () => {
    const scroll = useScroll();
    useFrame((state) => {
        const r1 = scroll.range(0.9, 0.1);
        const opacity = THREE.MathUtils.smoothstep(scroll.offset, 0.9, 0.98);
        const footerGroup = state.scene.children.find(c => c.type === 'Group');
    });
    return null;
}

// Footer Component
const FooterGroup = () => {
    const groupRef = useRef();
    const scroll = useScroll();

    useFrame(() => {
        if (groupRef.current) {
            const visibility = THREE.MathUtils.smoothstep(scroll.offset, 0.85, 0.98);

            const scale = THREE.MathUtils.lerp(0.8, 1, visibility);
            groupRef.current.scale.setScalar(scale);

            groupRef.current.children.forEach(child => {
                if (child.material) {
                    child.material.opacity = visibility;
                    child.material.transparent = true;
                    // Neon Blink
                    const time = Date.now() * 0.002;
                    const pulse = (Math.sin(time) + 1) * 0.5 + 0.5;
                    if (child.material.emissive) {

                    }
                }
            });

            groupRef.current.visible = visibility > 0.01;
        }
    });

    return (
        <group ref={groupRef} position={[0, -55, 0]} visible={false}>
            <Text
                fontSize={0.9}
                position={[0, 1.2, 0]}
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.02}
                outlineColor="#550033"
            >
                © SAMAARAMBH 10.0
                <meshBasicMaterial attach="material" color="#FF0095" toneMapped={false} transparent />
            </Text>
            <Text
                fontSize={0.6}
                position={[0, -0.1, 0]}
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.05}
                outlineColor="#00e0e0"
            >
                Designed by ashwin | mohit
                <meshBasicMaterial attach="material" color="#00F5FF" toneMapped={false} transparent />
            </Text>
        </group >
    );
};

const Experience = () => {
    const stage = useStore(state => state.stage);
    const activeCategory = useStore(state => state.activeCategory);

    const wheelGroupRef = useRef();
    const carouselGroupRef = useRef();

    return (
        <>
            <ParallaxBackground />
            <OverlayUI stage={stage} activeCategory={activeCategory} />

            {/* Ticket Booth Overlay removed - now on separate page */}

            <DetailView />

            <div className="no-scrollbar" style={{ width: '100vw', height: '100vh', position: 'fixed', top: 0, left: 0 }}>
                <Canvas
                    dpr={typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : [1, 1.5]}
                    camera={{ position: [0, 0, 20], fov: 45 }}
                    gl={{ toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.2, alpha: true }}
                >
                    <Suspense fallback={null}>
                        {/* Environment Lighting */}
                        <Environment preset="city" blur={0.8} >
                            <Lightformer intensity={2} rotation-x={Math.PI / 2} position={[0, 5, -9]} scale={[10, 10, 1]} />
                        </Environment>

                        {/* Carnival Lights */}
                        <pointLight position={[15, 15, 15]} color="#00F5FF" intensity={3} distance={60} decay={2} />
                        <pointLight position={[-15, -10, 15]} color="#FF0095" intensity={3} distance={60} decay={2} />
                        <pointLight position={[0, 10, -10]} color="#4E2FB3" intensity={2} distance={40} decay={2} />

                        {/* Volumetric Fog */}
                        <fog attach="fog" args={['#0B0E2E', 10, 90]} />

                        {/* Logo Spotlight */}
                        <spotLight position={[0, 0, 15]} intensity={18} angle={0.4} penumbra={1} color="#ffffff" distance={30} />

                        {/* Responsive Camera Logic */}
                        <ResponsiveCamera />

                        {/* Background: Roller Coaster */}
                        <RollerCoaster />

                        <ScrollControls pages={7} damping={0.4}>
                            <ScrollAnimator wheelRef={wheelGroupRef} carouselRef={carouselGroupRef} />

                            {/* Wheel Wrapper */}
                            <group ref={wheelGroupRef} position={[0, -25, 0]}>
                                <Wheel />
                            </group>

                            {/* Sponsors Carousel Wrapper */}
                            <group ref={carouselGroupRef} position={[0, -45, 0]} scale={0}>
                                <SponsorCarousel />
                            </group>

                            {/* Footer Credit - 3D Group */}
                            <FooterGroup />
                        </ScrollControls>
                    </Suspense>
                </Canvas>
            </div>
        </>
    );
};

export default Experience;
